import {
  PDF_FONT_SIZE,
  ReportSectionItemContent,
  ReportSectionItemTable,
  type ReportSectionItemTableProps,
  type ReportTableCell,
  type ReportTableProps,
  SECTION_TITLE_BACKGROUND_COLOR,
} from '@/components';
import { AccountingTypeLabelModifier } from '@/features/master-table/models';
import type { LedgerBudgetAndExecutionSectionProps } from '@/features/mold/models/ledger';
import { useLocaledAmount } from '@/providers';
import { formatValueByUnit } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  LEDGER_BORDER,
  LEDGER_PADDING,
  getEmptySectionTableDataByHeader,
  getExpenditureBudgetPurposeTableName,
  getGarageBySubProjects,
  sortExpenditureItemsData,
  splitExpenditureCartsByAccountingType,
} from '../../../../utils';

export const BudgetBreakdownTable: FC<{
  project: LedgerBudgetAndExecutionSectionProps['project'];
}> = ({ project }) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();

  const expenditureCartSectionTableData = useMemo<{
    header: ReportSectionItemTableProps['header'];
    data: ReportSectionItemTableProps['data'];
  }>(() => {
    const expenditureCartsData = splitExpenditureCartsByAccountingType(
      getGarageBySubProjects(project?.subProjects ?? [])?.expenditureCarts ??
        [],
    ).flat();

    const header: ReportSectionItemTableProps['header'] = [
      { type: 'text', value: t('expenditure-cart'), ratio: 8 },
      { type: 'text', value: t('accounting-name'), ratio: 17 },
      { type: 'text', value: t('account-title'), ratio: 27 },
      {
        type: 'text',
        value: t('desired-amount'),
        align: 'center',
        ratio: 25,
      },
      { type: 'text', value: t('remark'), ratio: 29 },
    ];

    const data: ReportSectionItemTableProps['data'] =
      expenditureCartsData.flatMap((expenditureCart, expenditureCartIdx) => {
        const expenditureItems = expenditureCart.expenditureItems;
        const shouldShowHeader = expenditureCartIdx > 0;
        const hasExpenditureItems = expenditureItems.length > 0;
        const expenditureCartBorderBottom = hasExpenditureItems
          ? LEDGER_BORDER
          : 'none';

        return [
          ...(shouldShowHeader
            ? [
                {
                  row: header.map<ReportTableCell>((cell) => ({
                    ...cell,
                    wrapperStyle: {
                      backgroundColor: SECTION_TITLE_BACKGROUND_COLOR,
                    },
                  })),
                },
              ]
            : []),
          {
            rowStyle: {
              borderBottom: 'none',
            },
            row: [
              {
                type: 'text',
                textWrapperStyle: {
                  alignItems: 'flex-start',
                },
                value:
                  AccountingTypeLabelModifier[
                    expenditureCart.accountingType?.type ?? ''
                  ],
              },
              {
                type: 'text',
                value: expenditureCart.accountingType.name,
                wrapperStyle: { borderBottom: expenditureCartBorderBottom },
              },
              {
                type: 'text',
                value: expenditureCart.accountTitle?.name,
                wrapperStyle: { borderBottom: expenditureCartBorderBottom },
              },
              {
                type: 'text',
                value: formatValueByUnit(expenditureCart.desiredAmount, {
                  operation: 'round',
                  unit,
                }),
                align: 'right',
                wrapperStyle: { borderBottom: expenditureCartBorderBottom },
              },
              {
                type: 'text',
                value: expenditureCart.remark,
                wrapperStyle: { borderBottom: expenditureCartBorderBottom },
              },
            ] satisfies ReportTableCell[],
          },
          ...(hasExpenditureItems
            ? [
                {
                  rowStyle: {
                    backgroundColor: SECTION_TITLE_BACKGROUND_COLOR,
                    borderBottom: 'none',
                  },
                  row: [
                    {
                      type: 'text',
                      value: '',
                      wrapperStyle: {
                        backgroundColor: 'white',
                      },
                    },
                    {
                      colSpan: 2,
                      type: 'text',
                      value: t('expenditure-budget-caption'),
                      wrapperStyle: { borderBottom: LEDGER_BORDER },
                    },
                    {
                      type: 'text',
                      value: t('remark'),
                      wrapperStyle: { borderBottom: LEDGER_BORDER },
                    },
                    {
                      type: 'multiple',
                      wrapperStyle: { padding: 0, borderBottom: LEDGER_BORDER },
                      cell: {
                        flexCells: [
                          {
                            type: 'text',
                            value: t('finalized-amount'),
                            align: 'center',
                            textStyle: { fontSize: PDF_FONT_SIZE.XL },
                          },
                          {
                            type: 'text',
                            value: t('all-requested-amount'),
                            align: 'center',
                            textStyle: { fontSize: PDF_FONT_SIZE.XL },
                          },
                        ],
                      },
                    },
                  ],
                } satisfies ReportTableProps['data'][number],
              ]
            : []),
          ...sortExpenditureItemsData(expenditureItems).map<
            ReportTableProps['data'][number]
          >((expenditureItem, expenditureItemIdx) => {
            const isLastItem =
              expenditureItemIdx === expenditureItems.length - 1;
            const borderBottom = isLastItem ? 'none' : LEDGER_BORDER;
            return {
              rowStyle: {
                border: 'none',
                borderBottom: 'none',
              },
              row: [
                {
                  type: 'text',
                  value: '',
                },
                {
                  colSpan: 2,
                  type: 'multiple',
                  wrapperStyle: {
                    borderBottom,
                    padding: 0,
                  },
                  cell: {
                    type: 'flex',
                    vertical: true,
                    wrapperStyle: {
                      padding: LEDGER_PADDING,
                      justifyItems: 'flex-start',
                    },
                    cellRatios: [],
                    flexCells: [
                      {
                        type: 'text',
                        value: expenditureItem.budgetType.name,
                        textWrapperStyle: {
                          display: 'inline-block',
                          borderRadius: 4,
                          border: LEDGER_BORDER,
                        },
                        textStyle: { fontSize: PDF_FONT_SIZE.XL },
                      },
                      {
                        type: 'text',
                        wrapperStyle: {
                          border: 'none',
                        },
                        textStyle: { padding: 0, fontSize: PDF_FONT_SIZE.XL },
                        value: getExpenditureBudgetPurposeTableName(
                          expenditureItem.expenditureBudgetPurpose,
                          expenditureItem.expenditureBudgetPurposeDescription,
                        ),
                      },
                    ],
                  },
                },
                {
                  type: 'text',
                  value: expenditureItem.remark,
                  wrapperStyle: { borderBottom },
                },
                {
                  type: 'multiple',
                  wrapperStyle: {
                    padding: 0,
                    borderBottom,
                  },
                  cell: {
                    isFlexStyle: true,
                    wrapperStyle: { border: 'none' },
                    flexCells: [
                      {
                        type: 'text',
                        value: formatValueByUnit(
                          expenditureItem.finalizedAmount,
                          {
                            operation: 'round',
                            unit,
                          },
                        ),
                        wrapperStyle: {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          padding: LEDGER_PADDING,
                        },
                        textStyle: { fontSize: PDF_FONT_SIZE.XL },
                      },
                      {
                        type: 'text',
                        value: formatValueByUnit(
                          expenditureItem.requestedAmount,
                          {
                            operation: 'round',
                            unit,
                          },
                        ),
                        wrapperStyle: {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          padding: LEDGER_PADDING,
                        },
                        textStyle: { fontSize: PDF_FONT_SIZE.XL },
                      },
                    ],
                  },
                },
              ],
            };
          }),
        ];
      });

    return {
      header,
      data: expenditureCartsData.length
        ? data
        : getEmptySectionTableDataByHeader(header),
    };
  }, [project?.subProjects, t, unit]);

  return (
    <ReportSectionItemContent
      isTable
      value={
        <ReportSectionItemTable
          header={expenditureCartSectionTableData.header}
          data={expenditureCartSectionTableData.data}
        />
      }
    />
  );
};
